import { Box, Select, Option, FormControl, FormHelperText, FormLabel, Typography } from "@mui/joy";
import { Controller } from "react-hook-form";
import { useEffect } from "react";
import { TASK_STATUSES, COLOR_OPTIONS } from "./constants";
import { useAccentColors } from "../../../../hooks";
import { resolveAccentBackground, resolveAccentForeground } from "../../../../utils/accentColors";

// Task Status Select Component
export const TaskStatusSelect = ({ control, rules, name }: any) => (
    <Controller
        name={name}
        control={control}
        rules={rules}
        render={({ field, fieldState }) => (
            <FormControl error={!!fieldState.error} sx={{ mt: 0.8 }}>
                <Select
                    placeholder="Select status"
                    value={field.value}
                    slotProps={{ listbox: { sx: { zIndex: 1100 } } }}
                    onChange={(_, value) => field.onChange(value || "")}
                >
                    {TASK_STATUSES.map((status) => (
                        <Option key={status.id} value={status.id} sx={{ zIndex: 10000 }}>
                            <Box
                                sx={{
                                    display: "flex",
                                    alignItems: "center",
                                    gap: 1,
                                }}
                            >
                                <Box
                                    sx={{
                                        width: 12,
                                        height: 12,
                                        borderRadius: "50%",
                                        bgcolor: status.color,
                                    }}
                                />
                                {status.name}
                            </Box>
                        </Option>
                    ))}
                </Select>
                {fieldState.error && <FormHelperText>{fieldState.error.message}</FormHelperText>}
            </FormControl>
        )}
    />
);

// Color Picker Component — uses accent colors (LightHex / DarkHex by theme)
export const ColorPicker = ({ watch, setValue }: any) => {
    const { colors, mode, isLoading } = useAccentColors();
    const selected = watch("color");

    const options =
        colors.length > 0
            ? colors.map((c) => ({
                  value: c.Name,
                  label: c.Name,
                  bgColor: resolveAccentBackground(c, mode),
                  textColor: resolveAccentForeground(c, mode),
              }))
            : COLOR_OPTIONS.map((c) => ({
                  value: c.value,
                  label: c.label,
                  bgColor: c.bgColor,
                  textColor: c.textColor,
              }));

    useEffect(() => {
        if (!options.length) return;
        const stillValid = options.some(
            (o) => o.value.toLowerCase() === String(selected || "").toLowerCase(),
        );
        if (!stillValid) {
            setValue("color", options[0].value);
        }
    }, [options, selected, setValue]);

    return (
        <Box flex={1}>
            <FormLabel>Task Color</FormLabel>
            <Typography level="body-xs" sx={{ color: "text.secondary", mb: 1 }}>
                Calendar shade follows the active theme (light / dark)
            </Typography>
            <Box sx={{ display: "flex", flexWrap: "wrap", gap: 1, mt: 2 }}>
                {isLoading && !options.length
                    ? null
                    : options.map((colorOption) => {
                          const isSelected =
                              String(selected || "").toLowerCase() ===
                              colorOption.value.toLowerCase();
                          return (
                              <Box
                                  key={colorOption.value}
                                  title={colorOption.label}
                                  sx={{
                                      width: 22,
                                      height: 22,
                                      borderRadius: "50%",
                                      bgcolor: colorOption.bgColor,
                                      border: "2px solid",
                                      borderColor: isSelected
                                          ? colorOption.textColor
                                          : "transparent",
                                      cursor: "pointer",
                                      display: "flex",
                                      alignItems: "center",
                                      justifyContent: "center",
                                      transition: "all 0.2s ease",
                                      "&:hover": {
                                          transform: "scale(1.1)",
                                      },
                                  }}
                                  onClick={() => setValue("color", colorOption.value)}
                              />
                          );
                      })}
            </Box>
        </Box>
    );
};
